/**
 * #1043 — Dual-read helper voor vital-events.
 *
 * Sinds #970/#971 vult de archive-import alleen `events`-records (type
 * birth/death/baptism/burial), niet de legacy `person.*Date`/`person.*Place`
 * kolommen. Renderers die uitsluitend uit de legacy velden lezen
 * (TreeNode, DetailPanel) tonen daardoor "?" terwijl de Gebeurtenissen-tab
 * de data wél laat zien.
 *
 * Deze helper levert een verrijkte Person met legacy fields aangevuld
 * vanuit de bijbehorende vital events — en alleen wanneer de legacy field
 * leeg is (write-on-read fallback).
 */
import type { Person, GenealogyEvent } from '../types';
import { compareDatesUnknownLast, extractYear, formatDateDisplay } from './dateUtils';

type VitalType = 'birth' | 'death' | 'baptism' | 'burial';

function findVitalEvent(
  events: GenealogyEvent[],
  personId: string,
  type: VitalType
): GenealogyEvent | undefined {
  return events.find((e) => {
    if (e.type !== type) return false;
    const participants = e.participants || [];
    if (participants.length === 0) return true; // legacy events zonder participants
    return participants.some((p) => p.personId === personId && (p.role === 'subject' || !p.role));
  });
}

export function resolveEffectiveVitals(
  person: Person,
  events: GenealogyEvent[] | undefined | null
): Person {
  // Privacy: bij een geredigeerde persoon zijn `birthPlace`/`baptismDate`/`deathPlace`
  // BEWUST leeg gemaakt door de server. De write-on-read fallback hieronder vulde ze
  // daarna weer terug uit de events — precies de velden die de 100-jaarregel beschermt.
  // De server redigeert die events sinds deze fix ook, maar de fallback hoort hier
  // sowieso niet te draaien: een lege waarde van de server is hier geen "ontbrekende
  // data", het is een besluit.
  if ((person as unknown as { _privacy_redacted?: boolean })._privacy_redacted === true) {
    return person;
  }
  if (!events || events.length === 0) return person;
  const birth = findVitalEvent(events, person.id, 'birth');
  const death = findVitalEvent(events, person.id, 'death');
  const baptism = findVitalEvent(events, person.id, 'baptism');
  const burial = findVitalEvent(events, person.id, 'burial');

  if (!birth && !death && !baptism && !burial) return person;

  const result: Person = { ...person };
  if (!result.birthDate && birth?.date) result.birthDate = birth.date;
  if (!result.birthPlace && birth?.placeName) result.birthPlace = birth.placeName;
  if (!result.deathDate && death?.date) result.deathDate = death.date;
  if (!result.deathPlace && death?.placeName) result.deathPlace = death.placeName;
  if (!result.baptismDate && baptism?.date) result.baptismDate = baptism.date;
  if (!result.baptismPlace && baptism?.placeName) result.baptismPlace = baptism.placeName;
  if (!result.burialDate && burial?.date) result.burialDate = burial.date;
  if (!result.burialPlace && burial?.placeName) result.burialPlace = burial.placeName;
  return result;
}

/**
 * #1523 — de datum die een lijst/kiezer naast een persoon toont: geboorte, anders de
 * doop (ook een doop die alleen als gebeurtenis bestaat). Geredigeerde personen
 * krijgen geen doop uit events — zie resolveEffectiveVitals.
 */
export function geboorteOfDoop(p: Person): { date?: string; isBaptism: boolean } {
  const v = resolveEffectiveVitals(p, p.events);
  if (v.birthDate) return { date: v.birthDate, isBaptism: false };
  if (v.baptismDate) return { date: v.baptismDate, isBaptism: true };
  return { date: undefined, isBaptism: false };
}

/** '12 maart 1850' / 'ged. 4 mei 1804'; undefined als er niets is. */
export function geboorteOfDoopLabel(
  p: Person,
  t: (key: string) => string,
  lang: string
): string | undefined {
  return metDoopPrefix(p, t, (d) => formatDateDisplay(d, lang));
}

/**
 * '1850' / 'ged. 1804'; undefined als er geen jaartal is. `jaar` is de jaar-notatie
 * (standaard kaal; `formatYearForTree` houdt '~1850' vast waar een lijst dat al toonde).
 */
export function geboorteOfDoopJaar(
  p: Person,
  t: (key: string) => string,
  jaar: (date: string) => string | null = extractYear
): string | undefined {
  return metDoopPrefix(p, t, jaar);
}

function metDoopPrefix(
  p: Person,
  t: (key: string) => string,
  fmt: (date: string) => string | null
): string | undefined {
  const { date, isBaptism } = geboorteOfDoop(p);
  const s = date ? fmt(date) : null;
  if (!s) return undefined;
  return isBaptism ? `${t('rbBaptizedShort')} ${s}` : s;
}

/**
 * #1548 (NC #781) — spiegel van geboorteOfDoop: overlijden, anders de
 * begrafenis (ook een begrafenis die alleen als gebeurtenis bestaat).
 * Geredigeerde personen krijgen geen begrafenis uit events — zie
 * resolveEffectiveVitals.
 */
export function overlijdenOfBegrafenis(p: Person): { date?: string; isBurial: boolean } {
  const v = resolveEffectiveVitals(p, p.events);
  if (v.deathDate) return { date: v.deathDate, isBurial: false };
  if (v.burialDate) return { date: v.burialDate, isBurial: true };
  return { date: undefined, isBurial: false };
}

/** '5 januari 1850' / 'begr. 3 juni 1746'; undefined als er niets is. */
export function overlijdenOfBegrafenisLabel(
  p: Person,
  t: (key: string) => string,
  lang: string
): string | undefined {
  return metBegrafenisPrefix(p, t, (d) => formatDateDisplay(d, lang));
}

/** '1850' / 'begr. 1746'; undefined als er geen jaartal is. */
export function overlijdenOfBegrafenisJaar(
  p: Person,
  t: (key: string) => string,
  jaar: (date: string) => string | null = extractYear
): string | undefined {
  return metBegrafenisPrefix(p, t, jaar);
}

function metBegrafenisPrefix(
  p: Person,
  t: (key: string) => string,
  fmt: (date: string) => string | null
): string | undefined {
  const { date, isBurial } = overlijdenOfBegrafenis(p);
  const s = date ? fmt(date) : null;
  if (!s) return undefined;
  return isBurial ? `${t('rbBuriedShort')} ${s}` : s;
}

/**
 * NC #809 — religie, nationaliteit en woonplaats: één leesvolgorde voor kaart,
 * waaierkleur, pdf-kaarten, boomrapport en rapport. De kolommen
 * (`person.religion`/`nationality`) zijn legacy — op de replica bij 2 personen
 * gevuld, terwijl er 181 een religie-gebeurtenis hebben. Dus: de gebeurtenissen
 * eerst, chronologisch (zonder jaartal achteraan, verder in invoervolgorde); de
 * kolom (en voor religie het feit) alleen als terugval. Twee keer achter elkaar
 * dezelfde waarde telt één keer.
 */
type Kenmerk = 'religion' | 'nationality' | 'residence';

const platteTekst = (s?: string): string =>
  (s || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();

function uitGebeurtenissen(
  person: Partial<Person>,
  type: Kenmerk,
  waarde: (e: GenealogyEvent) => string
): string[] {
  // Zie resolveEffectiveVitals: wat de server bij afscherming leegmaakte, vullen we niet terug.
  if ((person as unknown as { _privacy_redacted?: boolean })._privacy_redacted === true) return [];
  const uit: string[] = [];
  (person.events || [])
    .filter((e) => e.type === type)
    .sort((a, b) => compareDatesUnknownLast(a.date, b.date, 1))
    .map(waarde)
    .filter(Boolean)
    .forEach((w) => {
      if (uit[uit.length - 1]?.toLowerCase() !== w.toLowerCase()) uit.push(w);
    });
  return uit;
}

/** De waarde staat in de titel; anders in het religieveld of — zo op de replica — in de notitie. */
export function effectieveReligies(person: Partial<Person>): string[] {
  const evs = uitGebeurtenissen(person, 'religion', (e) =>
    platteTekst(e.title || e.religion || e.description)
  );
  if (evs.length) return evs;
  const terugval = (person.religion || '').trim() || platteTekst(person.facts?.religion?.value);
  return terugval ? [terugval] : [];
}

export function effectieveNationaliteiten(person: Partial<Person>): string[] {
  const evs = uitGebeurtenissen(person, 'nationality', (e) =>
    platteTekst(e.title || e.description)
  );
  if (evs.length) return evs;
  const terugval = (person.nationality || '').trim();
  return terugval ? [terugval] : [];
}

/** De plaats van de gebeurtenis (de titel is meestal het kale "Woonplaats"); er is geen kolom. */
export function effectieveWoonplaatsen(person: Partial<Person>): string[] {
  return uitGebeurtenissen(person, 'residence', (e) => platteTekst(e.placeName || e.title || ''));
}
